import { Search, Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ObservabilityRange, TopologyNode } from '@/services/observability-api';
import { cn } from '@/lib/utils';
import { ArchitectureNodeFilter } from './ArchitectureNodeFilter';
import type { MetricOverlay, ProtocolFilter } from './types';

export interface ArchitectureExplorerToolbarProps {
  range: ObservabilityRange;
  protocol: ProtocolFilter;
  overlay: MetricOverlay;
  nodes: TopologyNode[];
  selectedNodeIds: string[];
  noVisibleNodes?: boolean;
  adjacentTo?: string | undefined;
  onRangeChange: (range: ObservabilityRange) => void;
  onProtocolChange: (protocol: ProtocolFilter) => void;
  onOverlayChange: (overlay: MetricOverlay) => void;
  onSearchResultSelect: (nodeId: string) => void;
  onVisibleNodesChange: (nodeIds: string[]) => void;
  onResetLayout: () => void;
  onOpenSettings: () => void;
}

const ranges: ObservabilityRange[] = ['15m', '1h', '6h', '24h'];
const protocolOptions: Array<{ value: ProtocolFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'http', label: 'HTTP' },
  { value: 'events', label: 'Events' },
  { value: 'live', label: 'Live connections' },
];
const overlayOptions: Array<{ value: MetricOverlay; label: string }> = [
  { value: 'traffic', label: 'Traffic' },
  { value: 'topology', label: 'Topology' },
  { value: 'latency', label: 'Latency' },
  { value: 'errors', label: 'Errors' },
];

function ToggleGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors',
            value === option.value
              ? 'bg-lavender-600 text-white'
              : 'bg-white text-neutral-600 hover:bg-neutral-100'
          )}
          aria-pressed={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function ArchitectureExplorerToolbar({
  range,
  protocol,
  overlay,
  nodes,
  selectedNodeIds,
  noVisibleNodes = false,
  adjacentTo,
  onRangeChange,
  onProtocolChange,
  onOverlayChange,
  onSearchResultSelect,
  onVisibleNodesChange,
  onResetLayout,
  onOpenSettings,
}: ArchitectureExplorerToolbarProps) {
  const [search, setSearch] = useState('');
  const matches = useMemo(
    () =>
      search
        ? nodes.filter((node) =>
            node.label.toLocaleLowerCase().includes(search.toLocaleLowerCase())
          )
        : [],
    [nodes, search]
  );

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <label className="text-sm font-medium text-neutral-700">
        Range
        <select
          value={range}
          onChange={(event) => onRangeChange(event.target.value as ObservabilityRange)}
          className="ml-2 rounded border border-neutral-300 bg-white px-2 py-1"
        >
          {ranges.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <ToggleGroup
        label="Protocol filter"
        value={protocol}
        options={protocolOptions}
        onChange={onProtocolChange}
      />
      <ArchitectureNodeFilter
        nodes={nodes}
        selectedNodeIds={selectedNodeIds}
        noVisibleNodes={noVisibleNodes}
        adjacentTo={adjacentTo}
        onChange={onVisibleNodesChange}
      />
      <ToggleGroup
        label="Metric overlay"
        value={overlay}
        options={overlayOptions}
        onChange={onOverlayChange}
      />
      <button
        type="button"
        onClick={onResetLayout}
        className="rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-100"
      >
        Reset layout
      </button>
      <label className="relative ml-auto min-w-52 text-sm text-neutral-700">
        <Search
          className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-neutral-400"
          aria-hidden="true"
        />
        <span className="sr-only">Find a service or topic</span>
        <input
          type="search"
          placeholder="Find a service or topic"
          className="w-full rounded border border-neutral-300 bg-white py-1.5 pl-8 pr-2"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {matches.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded border border-neutral-200 bg-white py-1 shadow-elevated">
            {matches.slice(0, 8).map((node) => (
              <li key={node.id}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                  onClick={() => {
                    onSearchResultSelect(node.id);
                    setSearch('');
                  }}
                >
                  {node.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </label>
      <button
        type="button"
        onClick={onOpenSettings}
        className="shrink-0 rounded-md border border-neutral-300 bg-white p-1.5 text-neutral-600 hover:bg-neutral-100"
        aria-label="Graph settings"
        title="Graph settings"
      >
        <Settings2 className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
